"use client";

/**
 * Supabase Auth Provider
 *
 * React context provider for Supabase authentication state.
 * Replaces ClerkProvider for auth state management.
 *
 * Features:
 * - Session refresh and auth state changes
 * - User organization loading from Prisma
 * - signIn, signUp, signOut, switchOrganization methods
 *
 * Architecture Rule: AU-01 - Auth Abstraction Layer
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { Session, User } from "@supabase/supabase-js";

import { identifyAnalyticsUser, resetAnalyticsUser } from "@/lib/analytics/event-manager";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";

// ============================================================================
// /api/auth/me request dedup
// ============================================================================
// Each /api/auth/me call is a remote round-trip (Supabase auth validation +
// a cross-region DB join, ~1-3s). React Strict Mode double-invokes effects in
// dev, and initAuth() + onAuthStateChange can both request user data, so the
// endpoint would otherwise fire 2-4x per page load. We collapse concurrent
// requests for the same user into one in-flight promise and briefly cache the
// result so rapid re-mounts reuse it instead of re-hitting the network.

interface MeResponse {
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
    isLyfeAdmin: boolean;
    activeOrganizationId: string | null;
  };
  organizations: Array<{
    organization: { id: string; name: string; slug: string; type: string };
    role: string;
    isDefault: boolean;
  }>;
}

const ME_CACHE_TTL_MS = 10_000;
let meInFlight: { userId: string; promise: Promise<MeResponse | null> } | null = null;
let meCache: { userId: string; data: MeResponse | null; at: number } | null = null;

/** Bust the /api/auth/me cache (call after sign-out or org switch). */
function invalidateMeCache(): void {
  meCache = null;
  meInFlight = null;
}

async function fetchMe(userId: string): Promise<MeResponse | null> {
  const now = Date.now();
  if (meCache && meCache.userId === userId && now - meCache.at < ME_CACHE_TTL_MS) {
    return meCache.data;
  }
  if (meInFlight && meInFlight.userId === userId) {
    return meInFlight.promise;
  }

  const promise = (async (): Promise<MeResponse | null> => {
    const response = await fetch("/api/auth/me", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      // 404 (not yet registered) and 401 (no/expired session) are expected — not errors.
      if (response.status === 404 || response.status === 401) return null;
      const errorData = await response.json().catch(() => ({}));
      console.error("[SupabaseAuthProvider] API error:", response.status, errorData);
      return null;
    }

    return (await response.json()) as MeResponse;
  })();

  meInFlight = { userId, promise };
  try {
    const data = await promise;
    meCache = { userId, data, at: Date.now() };
    return data;
  } finally {
    if (meInFlight?.promise === promise) meInFlight = null;
  }
}

// ============================================================================
// Types
// ============================================================================

export interface OrganizationData {
  id: string;
  name: string;
  slug: string;
  type: string;
  role: string;
  isDefault: boolean;
}

export interface UserData {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isLyfeAdmin: boolean;
  activeOrganizationId: string | null;
}

export interface SupabaseAuthContextValue {
  // Loading state
  isLoaded: boolean;
  isLoading: boolean;

  // Session state
  session: Session | null;
  supabaseUser: User | null;

  // App user data (from Prisma via AuthUser bridge)
  user: UserData | null;
  userId: string | null;

  // Organization state
  organizations: OrganizationData[];
  activeOrganization: OrganizationData | null;
  activeOrganizationId: string | null;

  // Auth state
  isSignedIn: boolean;

  // Auth actions
  signIn: (email: string, password: string) => Promise<{ error: Error | null; session?: Session | null }>;
  signUp: (
    email: string,
    password: string,
    metadata?: { name?: string }
  ) => Promise<{ error: Error | null; user?: User | null }>;
  signOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  setActiveOrganization: (organizationId: string) => Promise<void>;

  // Session refresh
  refreshSession: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const SupabaseAuthContext = createContext<SupabaseAuthContextValue | null>(null);

// ============================================================================
// Hook
// ============================================================================

export function useSupabaseAuth(): SupabaseAuthContextValue {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error("useSupabaseAuth must be used within a SupabaseAuthProvider");
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface SupabaseAuthProviderProps {
  children: React.ReactNode;
}

export function SupabaseAuthProvider({ children }: SupabaseAuthProviderProps) {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [user, setUser] = useState<UserData | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [activeOrganizationId, setActiveOrgId] = useState<string | null>(null);
  const [supabase, setSupabase] = useState<ReturnType<typeof getSupabaseBrowserClient>>(null);

  // Create browser client only on client side
  useEffect(() => {
    if (typeof window === "undefined") return;

    const client = getSupabaseBrowserClient();
    if (!client) {
      console.warn("[SupabaseAuthProvider] URL or anon key not configured");
      // Set isLoading to false so the app can render (without auth)
      setIsLoading(false);
      return;
    }

    setSupabase(client);
  }, []);

  // Fetch user data and organizations from our API
  const fetchUserData = useCallback(async (supabaseUserId: string) => {
    try {
      // Deduped + briefly cached so Strict Mode double-invokes and overlapping
      // auth events don't each trigger a fresh ~3s round-trip.
      const data = await fetchMe(supabaseUserId);

      if (!data) {
        // Not registered yet / no valid session — leave the app to continue.
        return;
      }

      setUser({
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        isLyfeAdmin: data.user.isLyfeAdmin,
        activeOrganizationId: data.user.activeOrganizationId,
      });

      setOrganizations(
        data.organizations.map(
          (org: {
            organization: { id: string; name: string; slug: string; type: string };
            role: string;
            isDefault: boolean;
          }) => ({
            id: org.organization.id,
            name: org.organization.name,
            slug: org.organization.slug,
            type: org.organization.type,
            role: org.role,
            isDefault: org.isDefault,
          })
        )
      );

      // Set active org from user's preference or default
      const activeOrgId =
        data.user.activeOrganizationId ||
        data.organizations.find((o: { isDefault: boolean }) => o.isDefault)?.organization.id ||
        data.organizations[0]?.organization.id;

      setActiveOrgId(activeOrgId);
    } catch (error) {
      console.error("[SupabaseAuthProvider] Error fetching user data:", error);
    }
  }, []);

  // Initialize auth state
  useEffect(() => {
    // Wait for supabase client to be created before initializing auth
    if (!supabase) {
      // Don't set isLoading=false here - wait for client to be ready
      return;
    }

    // Get initial session
    const initAuth = async () => {
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        setSession(initialSession);
        setSupabaseUser(initialSession?.user ?? null);

        if (initialSession?.user) {
          await fetchUserData(initialSession.user.id);
        }
      } catch (error) {
        console.error("[SupabaseAuthProvider] Init error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession);
      setSupabaseUser(newSession?.user ?? null);

      if (event === "SIGNED_IN" && newSession?.user) {
        await fetchUserData(newSession.user.id);
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setOrganizations([]);
        setActiveOrgId(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase, fetchUserData]);

  // Auth actions
  const signIn = useCallback(
    async (email: string, password: string): Promise<{ error: Error | null; session?: Session | null }> => {
      if (!supabase) {
        return { error: new Error("Supabase client not initialized") };
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        return { error: new Error(error.message) };
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();
      return { error: null, session };
    },
    [supabase]
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: { name?: string }
    ): Promise<{ error: Error | null; user?: User | null }> => {
      if (!supabase) {
        return { error: new Error("Supabase client not initialized") };
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) {
        return { error: new Error(error.message) };
      }

      // Create user in our database via API
      if (data.user) {
        try {
          await fetch("/api/auth/register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              supabaseUserId: data.user.id,
              email: data.user.email,
              name: metadata?.name,
            }),
          });
        } catch (regError) {
          console.error("[SupabaseAuthProvider] Registration error:", regError);
        }
      }

      return { error: null, user: data.user ?? null };
    },
    [supabase]
  );

  const signOut = useCallback(async () => {
    if (!supabase) return;

    try {
      resetAnalyticsUser();
      invalidateMeCache();
      await supabase.auth.signOut();
      // Clear demo/role cookies (no-ops if absent)
      document.cookie = "demo-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "demo-user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "user-role=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    } catch (error) {
      console.error("[SupabaseAuthProvider] Sign-out error:", error);
    } finally {
      // Hard navigation to /login. This prevents any protected page from
      // re-rendering in an unauthenticated state and flashing "Access Denied"
      // before the redirect lands. Skip the in-effect state clears — the next
      // page load starts from scratch.
      window.location.replace("/login");
    }
  }, [supabase]);

  const switchOrganization = useCallback(
    async (organizationId: string) => {
      if (!user) return;

      try {
        await fetch("/api/auth/switch-organization", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ organizationId }),
          credentials: "include",
        });

        // Active org changed server-side — drop the cached /api/auth/me payload.
        invalidateMeCache();
        setActiveOrgId(organizationId);
        setUser((prev) => (prev ? { ...prev, activeOrganizationId: organizationId } : null));
      } catch (error) {
        console.error("[SupabaseAuthProvider] Switch organization error:", error);
        throw error;
      }
    },
    [user]
  );

  const setActiveOrganization = switchOrganization;

  const refreshSession = useCallback(async () => {
    if (!supabase) return;

    const { data } = await supabase.auth.refreshSession();
    setSession(data.session);
    setSupabaseUser(data.session?.user ?? null);
  }, [supabase]);

  // Derived state
  const isLoaded = !isLoading;
  const isSignedIn = !!session && !!supabaseUser;
  const userId = user?.id ?? null;
  const activeOrganization = organizations.find((org) => org.id === activeOrganizationId) ?? null;

  // Keep PostHog person + traits in sync when session or profile changes
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isLoaded || !supabaseUser) return;

    identifyAnalyticsUser({
      distinctId: supabaseUser.id,
      traits: {
        email: supabaseUser.email ?? undefined,
        name: user?.name ?? (supabaseUser.user_metadata?.name as string | undefined),
        app_user_id: user?.id,
        organization_id: activeOrganizationId ?? undefined,
      },
    });
  }, [isLoaded, supabaseUser, user, activeOrganizationId]);

  // Context value
  const value: SupabaseAuthContextValue = useMemo(
    () => ({
      isLoaded,
      isLoading,
      session,
      supabaseUser,
      user,
      userId,
      organizations,
      activeOrganization,
      activeOrganizationId,
      isSignedIn,
      signIn,
      signUp,
      signOut,
      switchOrganization,
      setActiveOrganization,
      refreshSession,
    }),
    [
      isLoaded,
      isLoading,
      session,
      supabaseUser,
      user,
      userId,
      organizations,
      activeOrganization,
      activeOrganizationId,
      isSignedIn,
      signIn,
      signUp,
      signOut,
      switchOrganization,
      setActiveOrganization,
      refreshSession,
    ]
  );

  return <SupabaseAuthContext.Provider value={value}>{children}</SupabaseAuthContext.Provider>;
}

export default SupabaseAuthProvider;
